import React from 'react';
import {
  Box,
  Flex,
  Icon,
  Progress,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaUsers, FaProjectDiagram, FaBuilding } from 'react-icons/fa';
import { usePlanUsage } from '../../hooks/usePlanUsage';

const RESOURCE_META = {
  users: { label: 'Team members', icon: FaUsers, color: 'blue' },
  projects: { label: 'Projects', icon: FaProjectDiagram, color: 'purple' },
  clients: { label: 'Clients', icon: FaBuilding, color: 'teal' },
} as const;

type ResourceKey = keyof typeof RESOURCE_META;

function UsageMetricBar({
  label,
  current,
  limit,
  icon,
  color,
}: {
  label: string;
  current: number;
  limit: number;
  icon: React.ElementType;
  color: string;
}) {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const isUnlimited = !Number.isFinite(limit);
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((current / limit) * 100));
  const atLimit = !isUnlimited && current >= limit;

  return (
    <Box bg={cardBg} borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={3}>
        <Flex align="center" gap={2}>
          <Flex
            align="center"
            justify="center"
            w={8}
            h={8}
            borderRadius="md"
            bg={`${color}.100`}
            color={`${color}.600`}
          >
            <Icon as={icon} boxSize={3.5} />
          </Flex>
          <Text fontSize="sm" fontWeight="semibold">
            {label}
          </Text>
        </Flex>
        <Text fontSize="sm" fontWeight="bold" color={atLimit ? 'red.500' : mutedText}>
          {current}
          {!isUnlimited && ` / ${limit}`}
          {isUnlimited && ' ∞'}
        </Text>
      </Flex>
      {!isUnlimited && (
        <Progress
          value={pct}
          size="sm"
          borderRadius="full"
          colorScheme={atLimit ? 'red' : pct >= 80 ? 'orange' : color}
        />
      )}
    </Box>
  );
}

interface UsageMetricsProps {
  columns?: { base: number; md: number };
}

export const UsageMetrics: React.FC<UsageMetricsProps> = ({ columns = { base: 1, md: 3 } }) => {
  const { usage, limits, isLoading } = usePlanUsage();

  if (isLoading || !usage) {
    return <Text fontSize="sm">Loading usage...</Text>;
  }

  return (
    <SimpleGrid columns={columns} spacing={4}>
      {(Object.keys(RESOURCE_META) as ResourceKey[]).map((key) => {
        const meta = RESOURCE_META[key];
        const limitKey =
          key === 'users' ? 'maxUsers' : key === 'projects' ? 'maxProjects' : 'maxClients';
        return (
          <UsageMetricBar
            key={key}
            label={meta.label}
            icon={meta.icon}
            color={meta.color}
            current={usage[key]}
            limit={limits[limitKey]}
          />
        );
      })}
    </SimpleGrid>
  );
};

export default UsageMetrics;
